import { useState, useEffect } from 'react'
import { useQuery } from 'react-query'
import Card, { CardHeader, CardTitle, CardContent } from '../UI/Card'
import Button from '../UI/Button'
import LoadingSpinner from '../UI/LoadingSpinner'
import { X, Download, CheckCircle, Clock, AlertCircle, FileText } from 'lucide-react'
import { format } from 'date-fns'
import api from '../../services/api'
import { paymentsAPI } from '../../services/api'
import { organizationsAPI } from '../../services/organizationsAPI'

export default function InvoiceDetailModal({ isOpen, onClose, paymentId, organizationId, onDownload }) {
  const [payment, setPayment] = useState(null)

  // Fetch payment details
  const { data: paymentData, isLoading } = useQuery(
    ['payment', paymentId],
    () => paymentsAPI.getById(paymentId).then(r => r.data),
    {
      enabled: !!paymentId && isOpen,
      onSuccess: (data) => {
        setPayment(data?.data?.payment || null)
      },
      onError: (error) => {
        console.error('Error fetching payment:', error)
      }
    }
  )

  // Fetch organization details
  const { data: orgData } = useQuery(
    ['organization', organizationId],
    () => organizationsAPI.getById(organizationId),
    {
      enabled: !!organizationId && isOpen
    }
  )

  const organization = orgData?.data?.data?.organization || null

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const formatDate = (date) => {
    if (!date) return 'N/A'
    try {
      return format(new Date(date), 'MMM dd, yyyy')
    } catch {
      return 'Invalid date'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
      case 'paid':
        return <CheckCircle className="h-4 w-4 text-green-600" />
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-600" />
      case 'failed':
        return <AlertCircle className="h-4 w-4 text-red-600" />
      default:
        return <FileText className="h-4 w-4 text-gray-600" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'paid':
        return 'bg-green-100 text-green-800'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'failed':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return 'Paid'
      case 'pending':
        return 'Pending'
      case 'failed':
        return 'Failed'
      default:
        return status || 'Unknown'
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-xl font-semibold">Invoice Details</CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : payment ? (
            <div className="space-y-6 bg-white">
              {/* Top Section - Company Name (Left) and INVOICE Title (Right) - Matching PDF Layout */}
              <div className="flex items-start justify-between border-b border-gray-200 pb-6">
                <div>
                  {/* Company Name - Large, Bold */}
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    {organization?.name || 'N/A'}
                  </h1>
                  {/* Email below company name */}
                  <p className="text-base text-gray-700">
                    {organization?.email || 'N/A'}
                  </p>
                </div>
                {/* INVOICE title on the right - Large, Bold */}
                <h1 className="text-4xl font-bold text-gray-900">
                  INVOICE
                </h1>
              </div>

              {/* Invoice # and Date on the left */}
              <div className="space-y-2">
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Invoice #:</span> {payment.transaction_id || `SUB-${payment.id?.substring(0, 8).toUpperCase() || 'N/A'}`}
                </p>
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Date:</span> {formatDate(payment.payment_date || payment.created_at)}
                </p>
              </div>

              {/* Product Table - Matching PDF Layout */}
              <div className="mt-6">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-300">
                        Product Name
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b border-gray-300">
                        Billing Period
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700 border-b border-gray-300">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-200">
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {payment.product_name || 'N/A'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {payment.plan_name ? `${payment.plan_name} - ${(payment.billing_cycle || 'Monthly').charAt(0).toUpperCase() + (payment.billing_cycle || 'Monthly').slice(1)} Plan` : 'Monthly Plan'}
                      </td>
                      <td className="px-4 py-4 text-sm font-medium text-gray-900 text-right">
                        {formatCurrency(payment.amount, payment.currency)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Status, Created Date, and Due Date - Matching PDF Layout */}
              <div className="mt-6 space-y-2">
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Status:</span> {getStatusLabel(payment.payment_status)}
                </p>
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Created Date:</span> {formatDate(payment.payment_date || payment.created_at)}
                </p>
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Due Date:</span> {(() => {
                    const dueDate = new Date(payment.payment_date || payment.created_at)
                    dueDate.setDate(dueDate.getDate() + 30)
                    return formatDate(dueDate)
                  })()}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
                <Button variant="outline" onClick={onClose}>
                  Close
                </Button>
                <Button
                  onClick={() => {
                    if (onDownload) {
                      onDownload(payment)
                    }
                  }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">Invoice not found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

